<script setup>
import FixPanel from "@/components/approval-center/partials/ui/FixPanel.vue";
import FixPanelHeaderTitle from "@/components/approval-center/partials/ui/FixPanelHeaderTitle.vue";
import FixPanelHeader from "@/components/approval-center/partials/ui/FixPanelHeader.vue";
import MoveGroupTransition from "@/components/transitions/MoveGroupTransition.vue";
import FixPanelIcon from "@/components/approval-center/partials/ui/FixPanelIcon.vue";
import FixContent from "@/components/approval-center/partials/ui/FixContent.vue";
import FixText from "@/components/approval-center/partials/ui/FixText.vue";
import KitIcon from "@/components/approval-center/partials/kit/KitIcon.vue";
import useApprovalFlow from "@/composables/approval-center/useApprovalFlow";
import SuccessFlow from "@/components/approval-center/modals/SuccessFlow.vue";
import RevisionFlow from "@/components/approval-center/modals/RevisionFlow.vue";
import {useApprovals} from "@/composables/approval-center/useApprovals";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import LoadMoreButton from "@/components/approval-center/partials/buttons/LoadMoreButton.vue";
import FixPanelSubText from "@/components/approval-center/partials/ui/FixPanelSubText.vue";
import {computed} from "vue";
import {orderBy} from "lodash";
import FixPanelFooter from "@/components/approval-center/partials/ui/FixPanelFooter.vue";

const props = defineProps({
  category: {
    type: String,
    required: true,
  },
});

const {
  showSuccessFlow,
  showRevisionFlow,
  handleConfirmMutation,
  handleConfirmRevisionMutation,
  handleClose,
  selectedApproval,
  selectApprovalForApprove,
  selectApprovalForRevision,
  revisionText,
} = useApprovalFlow();

const {mutate: handleConfirm} = handleConfirmMutation();
const {mutate: handleRevisionMutate, isPending: isRevisionPending} = handleConfirmRevisionMutation();

const {approvals, isPending} = useApprovals(props.category);

// Need to compute each approval input/output into a single dataset to only show edited id's
const mappedApprovals = computed(() => {
  if (!approvals.value || approvals.value.length < 1) {
    return [];
  }

  return approvals.value
    .map((approval) => {
      const {approvable} = approval;
      const changes = approvable?.output
        .filter((output) => output.was_updated)
        .map((output) => ({
          ...output,
          original: approvable.input.content.find((input) => input.id === output.id).content
        }));

      return {
        ...approval,
        pagePath: approvable.input.page_path,
        changes: orderBy(changes, ['id'], ['asc']),
      };
    });
})
</script>

<template>
  <FixPanel v-if="approvals && approvals.length">
    <FixPanelHeader class="tw-flex-col !tw-items-start">
      <FixPanelHeaderTitle>Website Copy</FixPanelHeaderTitle>
      <FixPanelSubText>Here’s the new website copy I’ve created to boost your SEO, please review and approve.</FixPanelSubText>
    </FixPanelHeader>

    <LoadingBlock v-if="isPending" />

    <MoveGroupTransition
      v-else
      class="tw-space-y-8"
    >
      <FixPanel
        v-for="approval in mappedApprovals"
        :key="approval.id"
      >
        <div class="tw-flex lg:tw-flex-row tw-flex-col tw-gap-16 tw-justify-between tw-items-stretch">
          <div class="tw-flex tw-flex-col tw-w-full tw-gap-6 tw-min-w-0">
            <FixPanelHeader>
              <FixPanelIcon icon-path="icons/settings-cog" />
              <FixPanelHeaderTitle>Before - {{ approval.pagePath }}</FixPanelHeaderTitle>
            </FixPanelHeader>

            <FixContent
              variant="secondary"
              class="tw-max-h-[480px]"
            >
              <FixText class="tw-space-y-4">
                <p
                  v-for="(item, key) in approval.changes"
                  :key="key"
                  v-html="item.original + (key < approval.changes.length - 1 ? `<hr class='tw-border-neutral-500 tw-mt-4'>` : '')"
                />
              </FixText>
            </FixContent>
          </div>

          <svg-vue
            icon="icons/arrow-right"
            class="tw-h-8 tw-w-8 tw-text-primary tw-self-center tw-shrink-0"
          />

          <div class="tw-flex tw-flex-col tw-w-full tw-gap-6 tw-min-w-0">
            <FixPanelHeader>
              <FixPanelIcon icon-path="icons/revision" />
              <FixPanelHeaderTitle>Revised</FixPanelHeaderTitle>
            </FixPanelHeader>

            <FixContent class="tw-flex tw-items-start tw-gap-8 tw-max-h-[480px]">
              <KitIcon />

              <FixText class="tw-space-y-4">
                <p
                  v-for="(item, key) in approval.changes"
                  :key="key"
                  v-html="item.updated + (key < approval.changes.length - 1 ? `<hr class='tw-border-primary tw-mt-4'>` : '')"
                />
              </FixText>
            </FixContent>
          </div>
        </div>

        <FixPanelFooter
          :approval="approval"
          :category="category"
          @approve="selectApprovalForApprove(approval)"
          @revise="selectApprovalForRevision(approval)"
        />
      </FixPanel>
    </MoveGroupTransition>
    <LoadMoreButton :category="category" />
  </FixPanel>

  <SuccessFlow
    v-if="showSuccessFlow"
    @confirm="handleConfirm({
      approval: selectedApproval,
      category: category,
    })"
    @close="handleClose"
  />

  <RevisionFlow
    v-if="showRevisionFlow"
    v-model:feedback="revisionText"
    :loading="isRevisionPending"
    @confirm="handleRevisionMutate({
      approval: selectedApproval,
      feedback: revisionText,
      category,
    })"
    @close="handleClose"
    @continue="handleClose"
  />
</template>
